import { useEffect, useState, ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  deleteHrDepartment,
  deleteHrDesignation,
  deleteHrHoliday,
  fetchHrHolidays,
  fetchHrOrg,
  fetchLeaveTypes,
  upsertHrDepartment,
  upsertHrDesignation,
  upsertHrHoliday,
  upsertLeaveType,
} from "../../store/hr/hrSlice";
import { useHrAccess } from "../../hooks/useHrAccess";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminPrimaryButton,
  AdminSoftInput,
  AdminSoftSelect,
  AdminTableCard,
  adminTableCell,
  adminTableHead,
} from "../../components/admin/AdminPageShell";
import { CustomDatePicker } from "../../components/common/DatePickers";

const Organization = () => {
  const dispatch = useAppDispatch();
  const { isHrAdmin } = useHrAccess();
  const { departments, designations, holidays, leaveTypes } = useAppSelector(
    (state) => state.hr,
  );

  const [deptName, setDeptName] = useState("");
  const [deptCode, setDeptCode] = useState("");
  const [desigName, setDesigName] = useState("");
  const [desigLevel, setDesigLevel] = useState("1");
  const [holidayName, setHolidayName] = useState("");
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayType, setHolidayType] = useState("company");
  const [ltName, setLtName] = useState("");
  const [ltCode, setLtCode] = useState("");
  const [ltDays, setLtDays] = useState("12");

  const load = () => {
    dispatch(fetchHrOrg());
    dispatch(fetchHrHolidays(new Date().getFullYear()));
    dispatch(fetchLeaveTypes());
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <>
      <Helmet>
        <title>Organization</title>
      </Helmet>
      <AdminPageShell accent="#345952">
        <AdminPageHeader
          title="Organization setup"
          subtitle="Departments, designations, company holidays, and leave types. PL +1.50 and CL +0.75 accrue monthly. Holiday quota is assigned per employee on Leaves. Company holidays are not requested by employees."
        />

        <section className="grid gap-4 xl:grid-cols-2">
          <Card title="Departments">
            {isHrAdmin ? (
              <Row>
                <AdminSoftInput placeholder="Name" value={deptName} onChange={(e) => setDeptName(e.target.value)} />
                <AdminSoftInput placeholder="Code" value={deptCode} onChange={(e) => setDeptCode(e.target.value)} />
                <AdminPrimaryButton
                  disabled={!deptName.trim() || !deptCode.trim()}
                  onClick={() =>
                    dispatch(upsertHrDepartment({ name: deptName, code: deptCode })).then((result) => {
                      if (upsertHrDepartment.fulfilled.match(result)) {
                        setDeptName("");
                        setDeptCode("");
                        load();
                      }
                    })
                  }
                >
                  Add
                </AdminPrimaryButton>
              </Row>
            ) : null}
            <SimpleTable
              headers={["Name", "Code", ""]}
              rows={departments.map((d) => [
                d.name,
                d.code,
                isHrAdmin ? (
                  <button
                    key={d.department_id}
                    type="button"
                    className="text-xs text-red-600"
                    onClick={() => dispatch(deleteHrDepartment(d.department_id)).then(load)}
                  >
                    Remove
                  </button>
                ) : (
                  ""
                ),
              ])}
            />
          </Card>

          <Card title="Designations">
            {isHrAdmin ? (
              <Row>
                <AdminSoftInput placeholder="Name" value={desigName} onChange={(e) => setDesigName(e.target.value)} />
                <AdminSoftInput placeholder="Level" value={desigLevel} onChange={(e) => setDesigLevel(e.target.value)} />
                <AdminPrimaryButton
                  disabled={!desigName.trim()}
                  onClick={() =>
                    dispatch(
                      upsertHrDesignation({ name: desigName, level: Number(desigLevel) }),
                    ).then((result) => {
                      if (upsertHrDesignation.fulfilled.match(result)) {
                        setDesigName("");
                        load();
                      }
                    })
                  }
                >
                  Add
                </AdminPrimaryButton>
              </Row>
            ) : null}
            <SimpleTable
              headers={["Name", "Level", ""]}
              rows={designations.map((d) => [
                d.name,
                String(d.level),
                isHrAdmin ? (
                  <button
                    key={d.designation_id}
                    type="button"
                    className="text-xs text-red-600"
                    onClick={() => dispatch(deleteHrDesignation(d.designation_id)).then(load)}
                  >
                    Remove
                  </button>
                ) : (
                  ""
                ),
              ])}
            />
          </Card>

          <Card title="Holidays">
            {isHrAdmin ? (
              <Row>
                <AdminSoftInput placeholder="Name" value={holidayName} onChange={(e) => setHolidayName(e.target.value)} />
                <CustomDatePicker
                  label="Date"
                  value={holidayDate}
                  onChange={setHolidayDate}
                  className="min-w-[180px]"
                />
                <AdminSoftSelect value={holidayType} onChange={(e) => setHolidayType(e.target.value)}>
                  <option value="company">Company</option>
                  <option value="national">National</option>
                  <option value="optional">Optional</option>
                </AdminSoftSelect>
                <AdminPrimaryButton
                  disabled={!holidayName.trim() || !holidayDate}
                  onClick={() =>
                    dispatch(
                      upsertHrHoliday({
                        name: holidayName,
                        holiday_date: holidayDate,
                        type: holidayType as "company" | "national" | "optional",
                      }),
                    ).then((result) => {
                      if (upsertHrHoliday.fulfilled.match(result)) {
                        setHolidayName("");
                        load();
                      }
                    })
                  }
                >
                  Add
                </AdminPrimaryButton>
              </Row>
            ) : null}
            <SimpleTable
              headers={["Date", "Name", "Type", ""]}
              rows={holidays.map((h) => [
                h.holiday_date,
                h.name,
                h.type,
                isHrAdmin ? (
                  <button
                    key={h.holiday_id}
                    type="button"
                    className="text-xs text-red-600"
                    onClick={() => dispatch(deleteHrHoliday(h.holiday_id)).then(load)}
                  >
                    Remove
                  </button>
                ) : (
                  ""
                ),
              ])}
            />
          </Card>

          <Card title="Leave types">
            {isHrAdmin ? (
              <Row>
                <AdminSoftInput placeholder="Name" value={ltName} onChange={(e) => setLtName(e.target.value)} />
                <AdminSoftInput placeholder="Code" value={ltCode} onChange={(e) => setLtCode(e.target.value)} />
                <AdminSoftInput placeholder="Days / year" value={ltDays} onChange={(e) => setLtDays(e.target.value)} />
                <AdminPrimaryButton
                  disabled={!ltName.trim() || !ltCode.trim()}
                  onClick={() =>
                    dispatch(
                      upsertLeaveType({
                        name: ltName,
                        code: ltCode,
                        max_days_year: Number(ltDays),
                        is_paid: true,
                      }),
                    ).then((result) => {
                      if (upsertLeaveType.fulfilled.match(result)) {
                        setLtName("");
                        setLtCode("");
                        load();
                      }
                    })
                  }
                >
                  Add
                </AdminPrimaryButton>
              </Row>
            ) : null}
            <p className="mb-3 text-xs text-[#8A8A85]">
              Policy types are PL, CL, Holiday quota, and Unpaid. Day counts are set per employee on Leaves or Opening balances.
            </p>
            <SimpleTable
              headers={["Name", "Code", "Suggested days", "Paid"]}
              rows={leaveTypes.map((t) => [
                t.name,
                t.code,
                String(t.max_days_year),
                t.is_paid ? "Yes" : "No",
              ])}
            />
          </Card>
        </section>
      </AdminPageShell>
    </>
  );
};

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-[#4A4A48]">{title}</h2>
      {children}
    </section>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div className="mb-3 flex flex-wrap gap-2">{children}</div>;
}

function SimpleTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: ReactNode[][];
}) {
  return (
    <AdminTableCard>
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
            {headers.map((h) => (
              <th key={h} className={adminTableHead}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[#F0F0EC]">
              {row.map((cell, j) => (
                <td key={j} className={adminTableCell}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </AdminTableCard>
  );
}

export default Organization;
